import { Component, DestroyRef, effect, inject, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { BaseModalComponent } from '../base-modal/base-modal.component';
import { ApiVerificationService } from '../../core/services/api-verification.service';
import { ConfigClientService } from '../../core/services/configClient.service';
import { ConfigService } from '../../core/services/config.service';

type ApiCheckState = '' | 'LOAD' | 'OK' | 'ERROR';

interface ApiSinInfo {
  Version?: string;
}

interface ApiIntegraInfo {
  version?: string;
}

@Component({
  selector: 'app-verificar-api',
  standalone: true,
  imports: [BaseModalComponent],
  templateUrl: './verificar-api.component.html',
  styleUrl: './verificar-api.component.scss',
})
export class VerificarApiComponent {
  private readonly apiVerificationService = inject(ApiVerificationService);
  private readonly configClientService = inject(ConfigClientService);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = model<boolean>(false);

  protected readonly stateSinergial = signal<ApiCheckState>('');
  protected readonly stateGpo = signal<ApiCheckState>('');
  protected readonly stateIntegra = signal<ApiCheckState>('');
  protected readonly statePagos = signal<ApiCheckState>('');

  protected readonly urlSinergial = this.configClientService.getConfig()?.api_sinergial ?? '';
  protected readonly urlGpo = this.configService.getAppConfig().api_gpo ?? '';
  protected readonly urlIntegra = this.configClientService.getConfig()?.api_integra ?? '';
  protected readonly urlPagos = this.configClientService.getConfig()?.api_pagos ?? '';

  protected readonly versionSinergial = this.resolveVersionSinergial();
  protected readonly versionIntegra = this.resolveVersionIntegra();

  private subscriptions: Subscription[] = [];

  constructor() {
    effect(
      () => {
        if (this.open()) {
          this.verificarApis();
        } else {
          this.resetStates();
          this.clearSubscriptions();
        }
      },
      { allowSignalWrites: true },
    );

    this.destroyRef.onDestroy(() => this.clearSubscriptions());
  }

  protected close(): void {
    this.open.set(false);
  }

  private verificarApis(): void {
    this.stateSinergial.set('LOAD');
    this.stateGpo.set('LOAD');

    this.subscriptions.push(
      this.apiVerificationService
        .verificarSinergial()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((ok) => this.stateSinergial.set(ok ? 'OK' : 'ERROR')),
      this.apiVerificationService
        .verificarGpo()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((ok) => this.stateGpo.set(ok ? 'OK' : 'ERROR')),
    );

    if (this.urlIntegra) {
      this.stateIntegra.set('LOAD');
      this.subscriptions.push(
        this.apiVerificationService
          .verificarIntegra()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((ok) => this.stateIntegra.set(ok ? 'OK' : 'ERROR')),
      );
    }

    if (this.urlPagos) {
      this.statePagos.set('LOAD');
      this.subscriptions.push(
        this.apiVerificationService
          .verificarPagos()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((ok) => this.statePagos.set(ok ? 'OK' : 'ERROR')),
      );
    }
  }

  private clearSubscriptions(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  private resetStates(): void {
    this.stateSinergial.set('');
    this.stateGpo.set('');
    this.stateIntegra.set('');
    this.statePagos.set('');
  }

  private resolveVersionSinergial(): string {
    try {
      const parsed = JSON.parse(localStorage.getItem('apiSINInfo') ?? '{}') as ApiSinInfo;
      return parsed.Version ?? 'ERROR';
    } catch {
      return 'ERROR';
    }
  }

  private resolveVersionIntegra(): string {
    try {
      const parsed = JSON.parse(localStorage.getItem('apiINTEGRAInfo') ?? '{}') as ApiIntegraInfo;
      return parsed.version ?? 'ERROR';
    } catch {
      return 'ERROR';
    }
  }

  protected stateLabel(state: ApiCheckState): string {
    switch (state) {
      case 'LOAD':
        return 'Verificando';
      case 'OK':
        return 'Operativa';
      case 'ERROR':
        return 'Error';
      default:
        return 'Pendiente';
    }
  }
}
